import React, { useMemo, useState } from "react";
import { Search, SearchX } from "lucide-react";
import JobCard from "../components/JobCard";

export const HomePage = ({ jobs, stats, onSelectJob, onGoToList }) => {
  const [q, setQ] = useState("");

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return jobs.filter((j) => {
      return (
        j.customerName.toLowerCase().includes(term) ||
        j.phone.toLowerCase().includes(term) ||
        j.device.toLowerCase().includes(term)
      );
    });
  }, [q, jobs]);

  return (
    <section
      data-testid="home-hero"
      className="hero-bg relative w-full"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16 text-center fade-in-up">
        {/* Stats pill — total beställningar */}
        <button
          data-testid="stats-pill"
          onClick={onGoToList}
          className="press-feedback stat-pulse inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E6F1FB] text-[#3B9EDA] text-sm sm:text-base font-semibold mb-6 hover:bg-[#d6e8f7] shadow-sm min-h-[40px]"
        >
          <Search className="h-4 w-4" />
          <span className="tabular-nums">
            {(stats?.total ?? 0).toLocaleString("sv-SE")}
          </span>
          <span>beställningar</span>
        </button>

        {/* Headline */}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold display-tight mb-4"
          style={{ color: "#0F172A", fontFamily: "Manrope, sans-serif" }}
          data-testid="home-headline"
        >
          Hantera dina <span style={{ color: "#3B9EDA" }}>reparationer</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          Sök, lägg till och följ upp serviceärenden för din verkstad — allt på
          ett ställe.
        </p>

        {/* Search bar */}
        <div className="max-w-2xl mx-auto relative">
          <div className="relative">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              data-testid="home-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="text"
              aria-label="Sök ärenden"
              placeholder="Sök t.ex. 'Anna' eller 'iPhone 15' eller '070…'"
              className="w-full rounded-full bg-white pl-14 pr-6 py-5 text-base shadow-[0_6px_24px_rgb(16,24,40,0.06)] border border-slate-100 focus:outline-none focus:ring-2 focus:ring-[#3B9EDA] focus:border-transparent transition min-h-[56px]"
            />
          </div>
        </div>

        {/* Stat bar */}
        <div
          data-testid="stats-bar"
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-8 text-sm text-slate-600"
        >
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ backgroundColor: "#3B9EDA" }} />
            <strong className="text-slate-900 tabular-nums">{stats?.aktiva ?? 0}</strong>
            aktiva ärenden
          </span>
          <span aria-hidden="true" className="h-3 w-px bg-slate-200" />
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-500" />
            <strong className="text-slate-900 tabular-nums">{stats?.Levererad ?? 0}</strong>
            levererade
          </span>
          <span aria-hidden="true" className="h-3 w-px bg-slate-200" />
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-rose-500" />
            <strong className="text-slate-900 tabular-nums">{stats?.Avbokad ?? 0}</strong>
            avbokade
          </span>
        </div>
      </div>

      {/* Search results */}
      {q.trim() && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {results.length === 0 ? (
            <div
              data-testid="home-empty-state"
              className="text-center py-16 text-slate-500"
            >
              <SearchX className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">
                Inga ärenden matchar "{q}".
              </p>
              <p className="text-sm mt-1">Försök med ett annat sökord.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-4">
                {results.length} resultat
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {results.map((j) => (
                  <JobCard key={j.id} job={j} onClick={onSelectJob} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default HomePage;
